import * as React from "react";
import { useQuery } from "@apollo/client";
import { Box, Typography, Skeleton, IconButton } from "@mui/material";
import Icon from "@mui/material/Icon";
import { ContainerCardLayout } from "components/haven-design";
import { UserDocuments, UserDocuments_viewer_documents as Document } from "types/gql-types";
import { documentsQuery as query } from "./queries";
import { formatUploadDate } from "./utls";
import UploadInline from "./upload";
// Removed custom styles in favor of MUI layout components

const documentIsRecent = (document: Document): boolean => {
  const uploadedAtMs = new Date(document.uploadedAt).getTime();
  return Date.now() - uploadedAtMs < 60_000; // within the last minute
};

type RowContainerProps = {
  children: React.ReactNode;
};

const RowContainer: React.FC<RowContainerProps> = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr auto" },
      columnGap: 2,
      rowGap: 0.5,
      py: 1.5,
      px: 1.5,
      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
    }}
  >
    {children}
  </Box>
);

const DocumentRow: React.FC<{ doc: Document }> = ({ doc }) => {
  const [isRecent, setIsRecent] = React.useState<boolean>(documentIsRecent(doc));

  React.useEffect(() => {
    if (!isRecent) return;
    const interval = setInterval(() => {
      setIsRecent(documentIsRecent(doc));
    }, 2000);
    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  }, [doc.uploadedAt, isRecent]);

  return (
    <RowContainer>
      <Typography variant="h6" data-cy="upload-name">
        <a href={doc.url} target="_blank" rel="noreferrer">{doc.name}</a>
        {isRecent ? " (processing...)" : ""}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {doc.category}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: { sm: "right" } }}
      >
        {formatUploadDate(doc.uploadedAt)}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
        {doc.downloadUrl ? (
          <IconButton
            component="a"
            href={doc.downloadUrl}
            aria-label="download"
            color="primary"
          >
            <Icon>download</Icon>
          </IconButton>
        ) : null}
      </Box>
    </RowContainer>
  );
};

const SkeletonRow: React.FC = () => (
  <RowContainer>
    <Skeleton variant="text" width="60%" height={28} />
    <Skeleton variant="text" width="40%" />
    <Skeleton variant="text" width="30%" />
  </RowContainer>
);

const LoadingSkeletonRows: React.FC = () => (
  <>
    {[0, 1].map((i) => (
      <SkeletonRow key={`skeleton-${i}`} />
    ))}
  </>
);

const DocumentList: React.FC = () => {
  const { data, loading, error } = useQuery<UserDocuments>(query);

  return (
    <ContainerCardLayout title="My Documents">
      <div style={{ margin: 16 }}>
        <Box sx={{ mb: 2 }}>
          <UploadInline />
        </Box>

        {loading && <LoadingSkeletonRows />}

        {error && (
          <Typography color="error">Error loading documents. Please contact support@havenconnect.com</Typography>
        )}

        {data?.viewer?.documents?.length === 0 && (
          <Typography color="text.secondary">No documents</Typography>
        )}

        {data?.viewer?.documents?.map((doc) => (
          <DocumentRow key={doc.id} doc={doc} />
        ))}
      </div>
    </ContainerCardLayout>
  );
};

export default DocumentList;
